import { useState } from 'react'
import { Icon } from '../ui/Icon'
import type { GroupView } from '../transport/relay'
import { POSTS_PER_SPARK, limitsLine } from './limitText'

/**
 * THE OWNER'S LIMITS, in the group's sheet — owner, 2026-10-20: group 的建立者可以在上面設定說一個使用者每天可以在
 * 上面發文幾次. 並且可以設定使用者他最多可以 attach 多少 agent 到 group 裡面.
 *
 * The owner gets a row that opens the two numbers; a member gets one line saying what they are held to (nothing,
 * when there are none). Empty means no limit — the same word the server keeps (null).
 */
export function GroupLimitsRow({ g, owner, onEdit }: { g: GroupView; owner: boolean; onEdit: () => void }) {
  const line = limitsLine(g.limits)
  if (!owner) return line ? <p className="gh-note grp-limits-line">{line}</p> : null
  // A server older than the limits sends none: no row to open onto numbers it would not keep.
  if (!g.limits) return null
  const { posts, agents } = g.limits
  const val =
    posts === null && agents === null
      ? 'None'
      : [posts !== null ? `${posts}/day` : null, agents !== null ? `${agents} agent${agents === 1 ? '' : 's'}` : null]
          .filter(Boolean)
          .join(' · ')
  return (
    <div className="ac-menu grp-menu">
      <button type="button" className="ac-row" onClick={onEdit}>
        <Icon name="tune" size={18} />
        <span className="ac-row-label">Posting limits</span>
        <span className="ac-row-val">{val}</span>
        <Icon name="chevron_right" size={18} className="ac-row-go" />
      </button>
    </div>
  )
}

const POSTS_MAX = 10000
const AGENTS_MAX = 100

/** '' → null (no limit); a whole number in range → it; anything else → undefined (not a limit). */
function parse(raw: string, max: number): number | null | undefined {
  const t = raw.trim()
  if (!t) return null
  if (!/^\d+$/.test(t)) return undefined
  const n = Number(t)
  return n <= max ? n : undefined
}

export function EditLimits({
  g,
  busy,
  onSave,
}: {
  g: GroupView
  busy: boolean
  onSave: (limits: { post_limit: number | null; agent_limit: number | null }) => void
}) {
  const [posts, setPosts] = useState(g.limits?.posts == null ? '' : String(g.limits.posts))
  const [agents, setAgents] = useState(g.limits?.agents == null ? '' : String(g.limits.agents))
  const p = parse(posts, POSTS_MAX)
  const a = parse(agents, AGENTS_MAX)
  const bad = p === undefined || a === undefined
  return (
    <form
      className="grp-limits"
      onSubmit={(e) => {
        e.preventDefault()
        if (!bad) onSave({ post_limit: p ?? null, agent_limit: a ?? null })
      }}
    >
      <label className="prof-field">
        <span>Posts per member, per day</span>
        <input
          type="text"
          inputMode="numeric"
          value={posts}
          placeholder="No limit"
          onChange={(e) => setPosts(e.target.value)}
          disabled={busy}
          aria-label="Posts per member, per day"
          aria-invalid={p === undefined}
        />
      </label>
      <p className="gh-note grp-limits-hint">
        A member’s agents post from the same allowance. Members can spend 1 Spark for {POSTS_PER_SPARK} more posts.
        The count starts again at midnight UTC.
      </p>
      <label className="prof-field">
        <span>Agents per member</span>
        <input
          type="text"
          inputMode="numeric"
          value={agents}
          placeholder="No limit"
          onChange={(e) => setAgents(e.target.value)}
          disabled={busy}
          aria-label="Agents per member"
          aria-invalid={a === undefined}
        />
      </label>
      <p className="gh-note grp-limits-hint">
        Leave a box empty for no limit. You, the owner, are not held to either. Agents already in the chat stay when you
        lower the number.
      </p>
      {bad && (
        <p className="gh-note mch-err" role="alert">
          Use a whole number — up to {POSTS_MAX.toLocaleString('en-US')} posts and {AGENTS_MAX} agents — or leave it empty.
        </p>
      )}
      <div className="grp-limits-acts">
        <button type="submit" className="sheet-btn primary" disabled={busy || bad}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}
